import { Box, Drawer as MuiDrawer, Button } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const DRAWER_WIDTH = 576

export const Drawer = styled(MuiDrawer)`
  .MuiPaper-root {
    background: ${theme.colors.surfaceContainerBackground};
    box-shadow: ${theme.attributes.boxShadow.selectFieldPaper};
    width: ${DRAWER_WIDTH}px;
    overflow-x: hidden;

    ::-webkit-scrollbar {
      width: 4px;
      background: transparent;
    }

    ::-webkit-scrollbar-track {
      box-shadow: none;
      background: ${theme.colors.strokePrimary};
    }

    ::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.cta};
      border-radius: ${theme.attributes.borderRadius.quaternary};
    }
  }
`

export const DrawerContent = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

export const DrawerHeader = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 40px 40px 0 40px;
  gap: 16px;

  .drawer-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 28px;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 42px;
  }

  .close-button {
    padding: 6px;
    min-width: unset;

    &:hover {
      background: rgba(202, 196, 208, 0.12);
    }

    svg path {
      stroke: ${theme.colors.menuItemText};
    }
  }
`

export const DrawerBody = styled(Box)`
  flex: 1;
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 4px;
    background: transparent;
  }

  ::-webkit-scrollbar-track {
    box-shadow: none;
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.borderMedium};
    border-radius: ${theme.attributes.borderRadius.quaternary};
  }
`

export const FormField = styled(Box)`
  position: relative;

  .MuiOutlinedInput-root {
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: transparent;
    height: 48px;

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.cta};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.cta};
      border-width: 1px;
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.dropdownBorder};
    }

    .MuiOutlinedInput-input {
      color: ${theme.colors.menuItemText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 14px;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 20px;
      padding: 14px 16px;

      &::placeholder {
        color: ${theme.colors.devicesSecondaryText};
        -webkit-text-fill-color: ${theme.colors.devicesSecondaryText};
        opacity: 1;
      }
    }
  }

  .MuiInputLabel-root {
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 12px;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 12px;
    transform: translate(14px, -9px) scale(1);
    background: ${theme.colors.surfaceContainerBackground};
    padding: 0 4px;

    &.Mui-focused {
      color: ${theme.colors.dropdownLabel};
    }
  }

  .MuiSelect-icon {
    color: ${theme.colors.devicesSecondaryText};
    right: 16px;

    &.MuiSelect-iconOpen {
      color: ${theme.colors.cta};
    }
  }

  .MuiFormHelperText-root {
    margin-left: 0;
    color: ${theme.colors.errorLight} !important;

    html[data-theme='light'] & {
      color: ${theme.colors.inputError} !important;
    }
  }

  /* Read-only rather than disabled, so the domain stays legible and selectable while it reads as locked. */
  &.locked-field {
    .MuiOutlinedInput-root {
      cursor: not-allowed;

      &:hover .MuiOutlinedInput-notchedOutline,
      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.dropdownBorder};
      }

      .MuiOutlinedInput-input {
        color: ${theme.colors.devicesSecondaryText};
        cursor: not-allowed;
      }
    }

    .MuiFormHelperText-root {
      color: ${theme.colors.devicesSecondaryText} !important;
    }
  }

  .info-icon {
    position: absolute;
    top: -11px;
    display: flex;
    align-items: center;
    padding-left: 2px;
    padding-right: 4px;
    background: ${theme.colors.surfaceContainerBackground};
    cursor: default;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover svg path {
      stroke: ${theme.colors.cta};
    }
  }

  .info-icon-sso {
    left: 72px;
  }

  .info-icon-roles {
    left: 79px;
  }

  .field-subtitle {
    margin-top: 8px;
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 16px;
  }
`

export const TextAreaField = styled(Box)`
  position: relative;

  .MuiFormHelperText-root {
    margin-left: 0;
    color: ${theme.colors.errorLight} !important;

    html[data-theme='light'] & {
      color: ${theme.colors.inputError} !important;
    }
  }

  .MuiOutlinedInput-root {
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: transparent;
    height: 128px;
    align-items: flex-start;
    padding: 0;
    overflow: hidden;

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.cta};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.cta};
      border-width: 1px;
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: ${theme.colors.dropdownBorder};
    }

    .MuiOutlinedInput-input {
      color: ${theme.colors.menuItemText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 14px;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 20px;
      padding: 16px;
      height: 100% !important;
      box-sizing: border-box;
      overflow-y: auto !important;
      word-break: break-all;
      resize: none;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${theme.colors.borderMedium};
        border-radius: 2px;
      }

      &::placeholder {
        color: ${theme.colors.devicesSecondaryText};
        -webkit-text-fill-color: ${theme.colors.devicesSecondaryText};
        opacity: 1;
      }
    }
  }

  .MuiInputLabel-root {
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 12px;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 12px;
    transform: translate(14px, -9px) scale(1);
    background: ${theme.colors.surfaceContainerBackground};
    padding: 0 4px;

    &.Mui-focused {
      color: ${theme.colors.dropdownLabel};
    }
  }
`

export const ReadOnlyField = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 48px;
  padding: 0 16px;
  border: 1px solid ${theme.colors.dropdownBorder};
  border-radius: ${theme.attributes.borderRadius.tertiary};

  .field-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 50px;

    .field-label {
      position: absolute;
      top: -8px;
      left: 10px;
      background: ${theme.colors.surfaceContainerBackground};
      padding: 0 4px;
      color: ${theme.colors.dropdownLabel};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 12px;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 12px;
    }

    .field-value {
      display: flex;
      align-items: center;
      flex: 1;
      color: ${theme.colors.menuItemText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 14px;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .copy-button {
    padding: 0;
    min-width: unset;
    width: 18px;
    height: 18px;

    &:hover {
      background: transparent;
    }

    svg {
      width: 18px;
      height: 18px;
    }

    svg path {
      stroke: ${theme.colors.dropdownLabel};
    }
  }
`

export const DomainClaimSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .claim-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 14px;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 22px;
  }

  .claim-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    border: 1px solid ${theme.colors.dropdownBorder};
  }
`

export const DrawerFooter = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 32px 40px;
  background: ${theme.colors.surfaceContainerBackground};
`

export const CancelButton = styled(Button)`
  flex: 1;
  height: 48px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  border: 1px solid ${theme.colors.dropdownBorder};
  background: transparent;
  text-transform: none;

  &:hover {
    background: rgba(78, 68, 75, 0.3);
    border-color: ${theme.colors.dropdownBorder};
  }

  .button-text {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 14px;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;
  }
`

export const SaveButton = styled(Button)`
  flex: 1;
  height: 48px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background-color: ${theme.colors.cta} !important;
  text-transform: none;

  &:hover {
    background-color: #c477c1 !important;
  }

  &.Mui-disabled {
    background-color: ${theme.colors.cta} !important;
    opacity: 0.5;

    .button-text {
      color: ${theme.colors.surfaceContainerBackground};
    }
  }

  html[data-theme='light'] & {
    &:hover {
      background-color: ${theme.colors.buttonHollowHover} !important;
    }

    &.Mui-disabled {
      background-color: ${theme.colors.cardFillTertiary} !important;
      opacity: 1;

      .button-text {
        color: ${theme.colors.btnSecondaryDisabledText};
      }
    }
  }

  .button-text {
    color: ${theme.colors.surfaceContainerBackground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 14px;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;
  }
`

export const DeleteDomainButton = styled(Button)`
  height: 48px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  border: 1px solid ${theme.colors.borderMedium};
  background: transparent;
  text-transform: none;
  padding: 13px 24px;

  &:hover {
    background: ${theme.colors.calendarBg};
    border-color: ${theme.colors.borderMedium};
  }

  .button-text {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 14px;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 22px;
    white-space: nowrap;
  }
`
